'use client';
import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { DialogClose, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { useStore } from '@/app/providers/store';
import type { ElementModality, ElementType, ImageFormState, TextFormState, VideoFormState } from '@/app/types';
import { cn, convertComponents2Assets } from '@/app/utils';
import { MediaElementForm } from './components/MediaElementForm';
import { TextElementForm } from './components/TextElementForm';
import { useSearchLabels } from './hooks/use-search-labels';

export const ElementDialog = ({ onCreateSuccess }: { onCreateSuccess?: () => void }) => {
  const { referenceModalMode, stateElement, reference, closeReferenceModal, referenceModalOpen } = useStore(
    (state) => ({
      reference: state.reference,
      stateElement: state.element,
      closeReferenceModal: state.closeReferenceModal,
      referenceModalMode: state.referenceModalMode,
      referenceModalOpen: state.referenceModalOpen,
    }),
  );

  const element = reference?.element || stateElement;

  const t = useTranslations();

  const { getLabels, labels, handleAddLabel } = useSearchLabels({
    currentType: referenceModalMode as 'edit' | 'create',
  });

  // 管理当前选择的 modality（仅在创建模式下）
  const [currentModality, setCurrentModality] = useState<ElementModality>('image');

  // 准备初始数据
  const initialDataMap = useMemo(() => {
    if (!element) {
      return {
        image: undefined,
        video: undefined,
        text: undefined,
      };
    }

    const baseData = {
      id: element.id,
      name: element.name,
      labels: element.labels,
    };

    const optionalAssets =
      'optionalAssets' in element && Array.isArray(element.optionalAssets) && element.optionalAssets?.length
        ? element.optionalAssets
        : element.components?.length
          ? convertComponents2Assets(element.components)
          : [];

    const { 0: first, 1: second, 2: third } = optionalAssets;

    const imageData: ImageFormState = {
      ...baseData,
      modality: 'image',
      optionalAssets: [first || null, second || null, third || null],
      voice: element.voice,
      style: element.recaption?.style,
      description: element.recaption?.description,
    };

    const videoData: VideoFormState = {
      ...baseData,
      modality: 'video',
      optionalAssets: [first || null, second || null, third || null],
      style: element.recaption?.style,
      description: element.recaption?.description,
    };

    const textData: TextFormState = {
      ...baseData,
      modality: 'text',
      description: element.recaption?.description,
    };

    return {
      image: imageData,
      video: videoData,
      text: textData,
    };
  }, [element]);

  // 初始化 modality
  useEffect(() => {
    if (referenceModalOpen) {
      if (referenceModalMode === 'create') {
        setCurrentModality('image');
      } else if (element?.modality) {
        setCurrentModality(element.modality);
      }
      getLabels();
    }
  }, [referenceModalOpen, referenceModalMode, element, getLabels]);

  const handleSave = useMemoizedFn((element: ElementType) => {
    if (referenceModalMode === 'create') {
      onCreateSuccess?.();
    }

    closeReferenceModal({
      reference: element,
      type: 'update',
    });
  });

  if (!referenceModalOpen || referenceModalMode === 'view') return null;

  return (
    <DialogContent
      className="flex h-full max-h-full w-full max-w-full items-center justify-center bg-transparent p-0 backdrop-blur-sm sm:h-full sm:w-full sm:max-w-full sm:p-0"
      onOpenAutoFocus={(e) => {
        e.preventDefault();
      }}
      closable={false}
      disableOverlayClose={false}
    >
      <div className="flex h-[738px] max-h-full min-h-[400px] min-w-[500px] max-w-[500px] flex-col gap-0 overflow-hidden rounded-12 border border-system-white12 bg-[#17191B] px-0 pt-3 pb-3 text-white shadow-[0_20px_60px_0_rgba(0,0,0,.32)] sm:h-full sm:min-h-full sm:w-full sm:min-w-fit sm:max-w-full sm:gap-0 sm:rounded-none sm:py-0 md:px-0">
        <DialogHeader className="flex shrink-0 items-center justify-center px-4 sm:h-16">
          <DialogTitle className={cn('relative w-full font-medium text-base')}>
            {t(`elements.elementDialog.title.${referenceModalMode as 'create' | 'edit'}`)}
            <DialogClose className="right-0" />
          </DialogTitle>
        </DialogHeader>

        {referenceModalMode === 'create' && (
          <Tabs
            value={currentModality}
            onValueChange={(val) => {
              setCurrentModality(val as ElementModality);
            }}
            className="w-full px-4 py-3"
          >
            <TabsList className="grid h-8 w-full grid-cols-3 bg-system-white04 px-0 py-0">
              <TabsTrigger
                value="image"
                className="rounded-8 py-2 text-system-white48 text-xs transition-all data-[state=active]:bg-system-white08 data-[state=active]:font-medium data-[state=active]:text-white"
              >
                {t('elements.elementDialog.modality.image')}
              </TabsTrigger>
              <TabsTrigger
                value="video"
                className="rounded-8 py-2 text-system-white48 text-xs transition-all data-[state=active]:bg-system-white08 data-[state=active]:font-medium data-[state=active]:text-white"
              >
                {t('elements.elementDialog.modality.video')}
              </TabsTrigger>
              <TabsTrigger
                value="text"
                className="rounded-8 py-2 text-system-white48 text-xs transition-all data-[state=active]:bg-system-white08 data-[state=active]:font-medium data-[state=active]:text-white"
              >
                {t('elements.elementDialog.modality.text')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        {currentModality === 'text' && (
          <TextElementForm
            initialData={initialDataMap.text}
            labels={labels}
            handleAddLabel={handleAddLabel}
            onSave={handleSave}
            isEditMode={referenceModalMode === 'edit'}
          />
        )}
        {currentModality === 'image' && (
          <MediaElementForm
            initialData={initialDataMap.image}
            labels={labels}
            handleAddLabel={handleAddLabel}
            onSave={handleSave}
            isEditMode={referenceModalMode === 'edit'}
          />
        )}
        {currentModality === 'video' && (
          <MediaElementForm
            initialData={initialDataMap.video}
            labels={labels}
            handleAddLabel={handleAddLabel}
            onSave={handleSave}
            isEditMode={referenceModalMode === 'edit'}
          />
        )}
      </div>
    </DialogContent>
  );
};
